import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import FormItem from 'components/form/FormItem';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useMediaQuery from 'hooks/useMediaQuery';

import BoxForm from 'pages/Settings/Box/BoxForm';

import { AUTOCOMPLETE } from 'services/consts';

import { Breakpoints } from 'utils/const.styles';

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import * as S from './BoxRow.styled';
import LengthUnitLotties from './LengthUnitLotties';

const boxColSpan = {
	lg: 3
};

const BoxRow = ({ remove, index, disabledDeleteButton, fieldName, required, onRemove, disabled }) => {
	const {
		control,
		setValue,
		formState: { errors }
	} = useFormContext();

	const isTablet = useMediaQuery(Breakpoints.tablet);

	const watchedBoxes = useWatch({
		control,
		name: `${fieldName}`
	});

	const watchedBox = useWatch({
		control,
		name: `${fieldName}[${index}]`
	});

	const boxAutocompleteColumns = useMemo(() => [{ accessor: 'name', width: 200 }], []);
	const boxAutocompleteLabels = useMemo(() => ['name'], []);

	const handleDelete = useCallback(() => {
		watchedBox.rowID && onRemove && onRemove(watchedBox.rowID);
		remove(index);
	}, []);

	const showLabels = useMemo(() => isTablet || index === 0, [isTablet, index]);

	return (
		<S.BoxContainer>
			<S.FlexContainer>
				<div style={{ flex: 1 }}>
					<FormItem label={showLabels && 'boxType'}>
						<AutoComplete
							name={`${fieldName}[${index}].name`}
							value={watchedBox.ID}
							autoCompleteName={AUTOCOMPLETE.BOXES}
							columns={boxAutocompleteColumns}
							selectionLabelAccessor={boxAutocompleteLabels}
							firstItem={{
								action: () =>
									NiceModal.show(BoxForm, {
										setBoxValues: (value) => {
											setValue(
												`${fieldName}[${index}]`,
												{
													...value,
													weight: watchedBox.weight
												},
												{
													shouldDirty: true,
													shouldValidate: true
												}
											);
										}
									}),
								label: <Typography color="var(--blue)">addANewBox</Typography>
							}}
							setValue={(value, isFirstRender) => {
								!isFirstRender &&
									setValue(
										value ? `${fieldName}[${index}]` : `${fieldName}[${index}].ID`,
										value
											? {
													...value,
													weight: watchedBox.weight
												}
											: value,
										{
											shouldDirty: true,
											shouldValidate: true
										}
									);
							}}
							textValue={watchedBox.ID ? undefined : (watchedBox.name ?? undefined)}
							disabled={disabled}
						/>
						{watchedBoxes?.length - 1 === index && (
							<Typography variant="caption" color="var(--content-3)" className="mt-2">
								fillDetailsManually
							</Typography>
						)}
					</FormItem>
				</div>
				<div style={{ flex: 2 }}>
					<S.BoxSizeContainer>
						<FormItem
							label={showLabels && 'length'}
							required={required}
							colSpan={boxColSpan}
							col={6}
							toolTip="priceCalculator.lengthTooltip">
							<Tooltip title={<LengthUnitLotties unit="length" />} placement="top" color="var(--white)">
								<IntegerInput
									disabled={disabled}
									min={0}
									unit="cm"
									name={`${fieldName}[${index}].length`}
								/>
							</Tooltip>
						</FormItem>
						<FormItem
							label={showLabels && 'width'}
							required={required}
							colSpan={boxColSpan}
							col={6}
							toolTip="priceCalculator.widthTooltip">
							<Tooltip title={<LengthUnitLotties unit="width" />} placement="top" color="var(--white)">
								<IntegerInput
									disabled={disabled}
									min={0}
									unit="cm"
									name={`${fieldName}[${index}].width`}
								/>
							</Tooltip>
						</FormItem>
						<FormItem
							label={showLabels && 'height'}
							required={required}
							colSpan={boxColSpan}
							col={6}
							toolTip="priceCalculator.heightTooltip">
							<Tooltip title={<LengthUnitLotties unit="height" />} placement="top" color="var(--white)">
								<IntegerInput
									disabled={disabled}
									min={0}
									unit="cm"
									name={`${fieldName}[${index}].height`}
								/>
							</Tooltip>
						</FormItem>
						<FormItem
							label={showLabels && 'weightKG'}
							required
							colSpan={boxColSpan}
							col={6}
							toolTip="priceCalculator.weightTooltip">
							<IntegerInput
								min={0}
								unit="kg"
								name={`${fieldName}[${index}].weight`}
								decimalLimit={1}
								allowFloat
								disabled={disabled}
								errorMessage={errors?.weight?.message}
							/>
						</FormItem>
					</S.BoxSizeContainer>
				</div>
			</S.FlexContainer>
			<S.ButtonContainer isFirst={index === 0}>
				<Button
					className="deleteButton"
					icon={faTrashCan}
					onClick={handleDelete}
					disabled={disabledDeleteButton || disabled}
					label="deleteBox"
					color="var(--red)"
					variant="transparent"
					css={css`
						border-color: var(--red) !important;
					`}
				/>
			</S.ButtonContainer>
		</S.BoxContainer>
	);
};

export default BoxRow;
